<template>
  <div class="notification-dropdown glass-dropdown" :class="{ 'no-footer': isOnNotificationsPage }">
    <!-- Header -->
    <div class="dropdown-header">
      <h3>Obavijesti</h3>
      <span class="unread-badge" v-if="unreadCount > 0">
        {{ unreadCount }} nepročitano
      </span>
    </div>

    <!-- Lista notifikacija -->
    <div class="notifications-list">
      <!-- Prazno stanje -->
      <div v-if="notifications.length === 0" class="empty-state">
        <v-icon large color="grey">mdi-bell-off-outline</v-icon>
        <p>Nemate notifikacija</p>
      </div>

      <!-- Notifikacije -->
      <div
        v-for="notification in displayNotifications"
        :key="notification.id"
        class="notification-item"
        :class="{ 'unread': !notification.readed }"
        @click="$emit('notification-click', notification)"
      >
        <!-- Ikona tip notifikacije -->
        <div class="notification-icon">
          <v-icon :color="getIconColor(notification.type)">
            {{ getIcon(notification.type) }}
          </v-icon>
        </div>

        <!-- Sadržaj -->
        <div class="notification-content">
          <div class="notification-subject">{{ notification.subject }}</div>
          <div class="notification-time">{{ formatTime(notification.created) }}</div>
        </div>

        <!-- Unread indicator -->
        <div v-if="!notification.readed" class="unread-dot"></div>
      </div>
    </div>

    <!-- Footer -->
    <div class="dropdown-footer">
      <a @click="$emit('show-all')" class="show-all-link">
        Pogledaj sve notifikacije
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotificationDropdown',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    notifications: {
      type: Array,
      default: () => []
    },
    unreadCount: {
      type: Number,
      default: 0
    }
  },
  computed: {
    displayNotifications () {
      // Prikaži max 7 notifikacija
      return this.notifications.slice(0, 7)
    },
    isOnNotificationsPage () {
      // Provjeri da li je trenutna ruta notifications stranica
      const currentRoute = this.$route?.name || ''
      return currentRoute.includes('notifications')
    },
    hasCompanyContext () {
      // Provjeri da li imamo companyId u ruti ili u state
      return !!(this.$route?.params?.companyId || this.$route?.meta?.sideNav)
    }
  },
  methods: {
    getIcon (type) {
      switch (type) {
        case 'CONFIRMATION': return 'mdi-alert-circle'
        case 'IMPORTANT': return 'mdi-information'
        case 'WARNING': return 'mdi-alert'
        case 'NOTIFICATION': return 'mdi-bell'
        default: return 'mdi-bell'
      }
    },

    getIconColor (type) {
      switch (type) {
        case 'CONFIRMATION': return '#f57c02'
        case 'IMPORTANT': return '#1577da'
        case 'WARNING': return '#f57c02'
        case 'NOTIFICATION': return '#3989c9'
        default: return '#3989c9'
      }
    },

    formatTime (timestamp) {
      if (!timestamp) return ''

      // timestamp je epoch u sekundama ili milisekundama
      let notifTime = typeof timestamp === 'number' ? timestamp : (timestamp.toMillis ? timestamp.toMillis() : timestamp)

      // Ako je epoch u sekundama (10 cifara), pretvori u milisekunde
      if (notifTime < 10000000000) {
        notifTime = notifTime * 1000
      }

      const date = new Date(notifTime)

      // Format: dd.mm.yyyy hh24:mi
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${day}.${month}.${year} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
.notification-dropdown {
  position: fixed;
  top: 70px;
  right: 16px;
  width: 380px;
  max-height: 500px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(57, 137, 201, 0.2);
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-dropdown.no-footer .notifications-list {
  padding-bottom: 12px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.05) 0%,
    rgba(57, 137, 201, 0.05) 50%,
    rgba(97, 84, 164, 0.05) 100%);
}

.dropdown-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.unread-badge {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.9));
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.notifications-list {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  padding: 8px 0;
}

.notifications-list::-webkit-scrollbar {
  width: 6px;
}

.notifications-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.notifications-list::-webkit-scrollbar-thumb {
  background-color: rgba(57, 137, 201, 0.3);
  border-radius: 3px;
  transition: background-color 0.3s ease;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(57, 137, 201, 0.5);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 3px solid transparent;
  position: relative;
}

.notification-item:hover {
  background: rgba(57, 137, 201, 0.08);
  border-left-color: rgba(57, 137, 201, 0.5);
}

.notification-item.unread {
  background: rgba(57, 137, 201, 0.05);
}

.notification-item.unread:hover {
  background: rgba(57, 137, 201, 0.12);
}

.notification-icon {
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: 2px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-subject {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-item.unread .notification-subject {
  font-weight: 600;
}

.notification-time {
  font-size: 12px;
  color: #666;
}

.unread-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  margin-left: 8px;
  margin-top: 6px;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
  }
}

.dropdown-footer {
  padding: 12px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  text-align: center;
  background: rgba(255, 255, 255, 0.5);
}

.show-all-link {
  color: #3989c9;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.show-all-link:hover {
  color: #00b3be;
  text-decoration: underline;
  gap: 6px;
}

.show-all-link:hover .v-icon {
  transform: translateX(2px);
}

.show-all-link .v-icon {
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
